import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { AnswerRow } from './types';
import { ANSWER_REPOSITORY } from '../ports/answer-repository.port';
import { ANSWER_DRAFT_CACHE } from '../ports/answer-draft-cache.port';

/**
 * Feature-scoped store for extraction answers. Provided by the page component.
 * Mirrors AnnotationStore's two-layer persistence: cache on every mutation,
 * explicit submit flushes to backend and clears cache, hydrate adopts a
 * differing cache and sets a recovered signal.
 */
@Injectable()
export class AnswerStore {
  /** Map from paperToProjectId -> current answer rows. */
  private readonly _answers = signal<Map<string, AnswerRow[]>>(new Map());

  /** Per-paper last-submitted snapshot for computing hasUnsavedChanges. */
  private readonly _submittedSnapshot = signal<Map<string, AnswerRow[]>>(new Map());

  /** Set of paperToProjectIds that were restored from the crash cache during hydration. */
  private readonly _recovered = signal<Set<string>>(new Set());

  private readonly repo = inject(ANSWER_REPOSITORY);
  private readonly cache = inject(ANSWER_DRAFT_CACHE);
  private _submitInFlight: Promise<void> | null = null;

  /** Read-only signal exposing the set of recovered paper keys. */
  readonly recovered: Signal<ReadonlySet<string>> = this._recovered.asReadonly();

  /** True if any tracked paper has unsaved changes relative to its submitted snapshot. */
  readonly hasUnsavedChanges: Signal<boolean> = computed(() => {
    const all = this._answers();
    const snaps = this._submittedSnapshot();
    for (const [paperId, rows] of all) {
      const snapshot = snaps.get(paperId) ?? [];
      if (!structurallyEqual(rows, snapshot)) return true;
    }
    for (const [paperId, snapshot] of snaps) {
      if (snapshot.length > 0) {
        const rows = all.get(paperId) ?? [];
        if (!structurallyEqual(rows, snapshot)) return true;
      }
    }
    return false;
  });

  /** Returns a signal-like function: call with no args to read answers for a paper. */
  answersFor(paperKey: string): Signal<AnswerRow[]> {
    return computed(() => this._answers().get(paperKey) ?? []);
  }

  /** Upsert a single answer row and write to cache. */
  setAnswer(paperKey: string, questionId: number, value: string): void {
    this._answers.update((m) => {
      const next = new Map(m);
      const rows = [...(next.get(paperKey) ?? [])];
      const idx = rows.findIndex((r) => r.questionId === questionId);
      if (idx >= 0) {
        rows[idx] = { questionId, value };
      } else {
        rows.push({ questionId, value });
      }
      next.set(paperKey, rows);
      return next;
    });
    this.persistDraft(paperKey);
  }

  /** Write current rows for a paper to the crash cache. */
  private persistDraft(paperKey: string): void {
    const rows = this._answers().get(paperKey) ?? [];
    this.cache.write(paperKey, rows);
  }

  /**
   * Load backend baseline for a paper, adopt cache if structurally different,
   * and set recovered signal if cache was adopted.
   */
  async hydrateFromRepository(paperKey: string): Promise<AnswerRow[]> {
    const loaded = await this.repo.loadFor(paperKey);

    this._answers.update((m) => {
      const next = new Map(m);
      next.set(paperKey, loaded);
      return next;
    });
    this._submittedSnapshot.update((m) => {
      const next = new Map(m);
      next.set(paperKey, [...loaded]);
      return next;
    });

    const cached = this.cache.read(paperKey);
    if (cached !== null && !structurallyEqual(cached, loaded)) {
      this._answers.update((m) => {
        const next = new Map(m);
        next.set(paperKey, cached);
        return next;
      });
      this._recovered.update((s) => new Set([...s, paperKey]));
    } else {
      this.cache.clear(paperKey);
    }

    return loaded;
  }

  /**
   * Push current answers for a paper to the repository. On success updates
   * the snapshot and clears the cache.
   * Single-flight: concurrent calls share the same in-flight promise.
   */
  submitChanges(paperKey: string): Promise<void> {
    if (this._submitInFlight) return this._submitInFlight;
    this._submitInFlight = (async () => {
      try {
        const current = this._answers().get(paperKey) ?? [];
        await this.repo.submitAll(paperKey, current);
        this._submittedSnapshot.update((m) => {
          const next = new Map(m);
          next.set(paperKey, [...current]);
          return next;
        });
        this.cache.clear(paperKey);
      } finally {
        this._submitInFlight = null;
      }
    })();
    return this._submitInFlight;
  }

  /** Discard local changes and restore the last-submitted snapshot. No repo call. */
  discardChanges(paperKey: string): void {
    const snapshot = this._submittedSnapshot().get(paperKey) ?? [];
    this._answers.update((m) => {
      const next = new Map(m);
      next.set(paperKey, [...snapshot]);
      return next;
    });
    this.cache.clear(paperKey);
  }

  /** Remove the given paper key from the recovered set after the page has handled it. */
  acknowledgeRecovery(paperKey: string): void {
    this._recovered.update((s) => {
      const next = new Set(s);
      next.delete(paperKey);
      return next;
    });
  }
}

/** Deep structural equality for AnswerRow arrays: compares by value of every field. */
function structurallyEqual(a: AnswerRow[], b: AnswerRow[]): boolean {
  if (a.length !== b.length) return false;
  const byId = new Map(b.map((r) => [r.questionId, r.value]));
  for (const x of a) {
    const val = byId.get(x.questionId);
    if (val === undefined) return false;
    if (val !== x.value) return false;
  }
  return true;
}
